import { useGetAdminCoursesAssessment } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Select from '@components/V4/Select/Select';
import { AcademicCapIcon, UserGroupIcon, UsersIcon } from '@heroicons/react/outline';
import {
  EmptyState,
  FilterField,
  MetricCard,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import * as ROUTES from '@routes';
import { CourseAssessmentDTO, CourseYearStatsDTO } from '@shared/apis/courses.apis';
import AgoraSearchBar from '@utils/AgoraSearchbar';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CourseSwatch, FlagPill } from './courseUi';

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top transition-colors';
const leftHead = 'px-4 py-3 text-left font-semibold';
const rightHead = 'px-4 py-3 text-right font-semibold';
const leftCell = 'px-4 py-3 text-left text-customLight';
const rightCell = 'px-4 py-3 text-right tabular-nums text-customLight';

type YearStats = Omit<CourseYearStatsDTO, 'year'>;
type SortKey = 'enrollments' | 'active' | 'completed' | 'completionRate' | 'title';

const SORT_OPTIONS: { label: string; value: SortKey }[] = [
  { label: 'Most enrollments', value: 'enrollments' },
  { label: 'Most in progress', value: 'active' },
  { label: 'Most completed', value: 'completed' },
  { label: 'Best completion rate', value: 'completionRate' },
  { label: 'Title', value: 'title' },
];

const EMPTY_STATS: YearStats = {
  enrollments: 0,
  students: 0,
  active: 0,
  completed: 0,
  cancelled: 0,
  suspended: 0,
  sessionsHeld: 0,
  mentors: 0,
};

const statsFor = (course: CourseAssessmentDTO, year: number | 'all'): YearStats =>
  year === 'all' ? course.allTime : course.years.find((row) => row.year === year) ?? EMPTY_STATS;

const sumStats = (rows: YearStats[]): YearStats => {
  const total = rows.reduce(
    (acc, row) => ({
      ...acc,
      enrollments: acc.enrollments + row.enrollments,
      students: acc.students + row.students,
      active: acc.active + row.active,
      completed: acc.completed + row.completed,
      cancelled: acc.cancelled + row.cancelled,
      suspended: acc.suspended + row.suspended,
      sessionsHeld: acc.sessionsHeld + row.sessionsHeld,
      mentors: acc.mentors + row.mentors,
    }),
    { ...EMPTY_STATS }
  );
  const ended = total.completed + total.cancelled;
  return {
    ...total,
    averageSessions: total.enrollments
      ? Math.round((total.sessionsHeld / total.enrollments) * 10) / 10
      : undefined,
    completionRate: ended ? Math.round((total.completed / ended) * 100) : undefined,
  };
};

const rateTone = (rate?: number) =>
  rate === undefined
    ? 'text-customGrey'
    : rate >= 75
    ? 'text-customGreen'
    : rate >= 50
    ? 'text-customGold'
    : 'text-customRed';

const StatsCells = (props: { stats: YearStats }) => {
  const { stats } = props;
  return (
    <>
      <td className={mergeClassNames(rightCell, 'font-semibold text-white')}>
        {stats.enrollments}
      </td>
      <td className={rightCell}>{stats.students}</td>
      <td className={rightCell}>
        <span className={stats.active ? 'text-customPrimary' : ''}>{stats.active}</span>
      </td>
      <td className={rightCell}>{stats.completed}</td>
      <td className={rightCell}>
        <span className={stats.cancelled ? 'text-customRed' : ''}>{stats.cancelled}</span>
        {stats.suspended ? (
          <span className="text-xs text-customGrey"> +{stats.suspended} paused</span>
        ) : null}
      </td>
      <td className={mergeClassNames(rightCell, rateTone(stats.completionRate))}>
        {stats.completionRate !== undefined ? `${stats.completionRate}%` : '—'}
      </td>
      <td className={rightCell}>
        {stats.sessionsHeld}
        {stats.averageSessions !== undefined ? (
          <span className="text-xs text-customGrey"> · {stats.averageSessions}/enr.</span>
        ) : null}
      </td>
      <td className={rightCell}>{stats.mentors}</td>
    </>
  );
};

const StatsHeaders = () => (
  <>
    <th className={rightHead}>Enrollments</th>
    <th className={rightHead}>Students</th>
    <th className={rightHead}>In progress</th>
    <th className={rightHead}>Completed</th>
    <th className={rightHead}>Cancelled</th>
    <th className={rightHead}>Completion</th>
    <th className={rightHead}>Sessions held</th>
    <th className={rightHead}>Mentors taught</th>
  </>
);

const CourseAssessment = (props: { courseId?: string }) => {
  const { courseId } = props;
  const history = useHistory();
  const { data, isLoading } = useGetAdminCoursesAssessment();
  const [year, setYear] = useState<number | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('enrollments');
  const [search, setSearch] = useState('');

  const yearOptions = useMemo(
    () => [
      { label: 'All time', value: 'all' },
      ...(data?.years ?? []).map((value) => ({ label: String(value), value })),
    ],
    [data]
  );

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    const withStats = data.courses
      .filter(
        (course) =>
          !needle ||
          course.title.toLowerCase().includes(needle) ||
          (course.abbreviation ?? '').toLowerCase().includes(needle)
      )
      .map((course) => ({ course, stats: statsFor(course, year) }));
    const sorters: Record<
      SortKey,
      (a: typeof withStats[number], b: typeof withStats[number]) => number
    > = {
      enrollments: (a, b) => b.stats.enrollments - a.stats.enrollments,
      active: (a, b) => b.stats.active - a.stats.active,
      completed: (a, b) => b.stats.completed - a.stats.completed,
      completionRate: (a, b) => (b.stats.completionRate ?? -1) - (a.stats.completionRate ?? -1),
      title: (a, b) => a.course.title.localeCompare(b.course.title),
    };
    return withStats.sort(
      (a, b) => sorters[sortKey](a, b) || a.course.title.localeCompare(b.course.title)
    );
  }, [data, year, sortKey, search]);

  const totals = useMemo(() => sumStats(rows.map((row) => row.stats)), [rows]);

  if (isLoading || !data) {
    return (
      <div className="flex h-48 items-center justify-center">
        <LoadingSpinner size="normal" />
      </div>
    );
  }

  if (courseId) {
    const course = data.courses.find((item) => item._id === courseId);
    if (!course) {
      return <EmptyState>No enrollment data for this course yet.</EmptyState>;
    }

    return (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
          <MetricCard
            label="Mentors who can teach it"
            value={course.mentorsCanTeach}
            icon={<AcademicCapIcon className="h-5 w-5" />}
            accent="teal"
          />
          <MetricCard
            label="Available now (active, under capacity)"
            value={course.mentorsAvailable}
            icon={<UserGroupIcon className="h-5 w-5" />}
            accent={course.mentorsAvailable ? 'green' : 'gold'}
          />
          <MetricCard
            label="Teaching it right now"
            value={course.mentorsTeachingNow}
            icon={<UsersIcon className="h-5 w-5" />}
            accent="lila"
          />
        </div>

        <SectionHeading
          title="Students by year"
          description="Year is the enrollment start date. In progress counts enrollments that are still active today, whatever year they started."
        />

        {!course.years.length ? (
          <EmptyState>Nobody has enrolled in this course yet.</EmptyState>
        ) : (
          <TableShell minWidth="min-w-[900px]">
            <thead>
              <tr className={headRow}>
                <th className={leftHead}>Year</th>
                <StatsHeaders />
              </tr>
            </thead>
            <tbody>
              {course.years.map((row) => (
                <tr key={row.year} className={bodyRow}>
                  <td className={mergeClassNames(leftCell, 'font-semibold text-white')}>
                    {row.year}
                  </td>
                  <StatsCells stats={row} />
                </tr>
              ))}
              <tr className={mergeClassNames(bodyRow, 'bg-white/5')}>
                <td className={mergeClassNames(leftCell, 'font-semibold text-white')}>All time</td>
                <StatsCells stats={course.allTime} />
              </tr>
            </tbody>
          </TableShell>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionHeading
        title="Product assessment"
        description={`How each course sells and performs: enrollments started in the selected year, how many finished or were cancelled, sessions delivered, and how many mentors can take new students. Computed ${moment(
          data.generatedAt
        ).fromNow()}.`}
      />

      <div className="flex flex-wrap items-end gap-4">
        <FilterField width="w-full tablet:w-72">
          <AgoraSearchBar
            search={search}
            handleSearch={setSearch}
            placeholder="Search by course title..."
          />
        </FilterField>
        <FilterField width="w-full tablet:w-44">
          <Select
            label="Year"
            variant="secondary"
            value={year}
            options={yearOptions}
            onSelect={(value) => setYear(value === 'all' ? 'all' : Number(value))}
          />
        </FilterField>
        <FilterField width="w-full tablet:w-56">
          <Select
            label="Sort by"
            variant="secondary"
            value={sortKey}
            options={SORT_OPTIONS}
            onSelect={(value) => setSortKey(value as SortKey)}
          />
        </FilterField>
      </div>

      {!rows.length ? (
        <EmptyState>No courses to assess.</EmptyState>
      ) : (
        <TableShell minWidth="min-w-[1240px]">
          <thead>
            <tr className={headRow}>
              <th className={leftHead}>Course</th>
              <StatsHeaders />
              <th className={rightHead}>Can teach</th>
              <th className={rightHead}>Available now</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ course, stats }) => (
              <tr
                key={course._id}
                className={mergeClassNames(bodyRow, 'cursor-pointer hover:bg-white/5')}
                onClick={() => history.push(`${ROUTES.MANAGE_COURSES}/${course._id}`)}
              >
                <td className={leftCell}>
                  <div className="flex items-center gap-3">
                    <CourseSwatch color={course.color} abbreviation={course.abbreviation} />
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="font-semibold text-white">{course.title}</span>
                      <div className="flex gap-1.5">
                        {course.isMentorless ? <FlagPill tone="warn">Mentorless</FlagPill> : null}
                      </div>
                    </div>
                  </div>
                </td>
                <StatsCells stats={stats} />
                <td className={rightCell}>{course.isMentorless ? '—' : course.mentorsCanTeach}</td>
                <td className={rightCell}>
                  {course.isMentorless ? (
                    '—'
                  ) : (
                    <span
                      className={mergeClassNames(
                        course.mentorsAvailable ? 'text-customGreen' : 'text-customRed'
                      )}
                    >
                      {course.mentorsAvailable}
                    </span>
                  )}
                  <span className="text-xs text-customGrey">
                    {' '}
                    · {course.mentorsTeachingNow} teaching
                  </span>
                </td>
              </tr>
            ))}
            <tr className={mergeClassNames(bodyRow, 'bg-white/5')}>
              <td className={mergeClassNames(leftCell, 'font-semibold text-white')}>
                All courses ({rows.length})
              </td>
              <StatsCells stats={totals} />
              <td className={rightCell}>—</td>
              <td className={rightCell}>—</td>
            </tr>
          </tbody>
        </TableShell>
      )}
    </div>
  );
};

export default CourseAssessment;
